import type { MultiSelectOption } from "@/components/ui/MultiSelect";
import {
  STATUS_LABELS,
  type SolicitacaoPagamentoDto,
  type SolicitacaoPagamentoStatus,
} from "@/services/solicitacaoPagamento";
import { currentLocalDateInput } from "@/utils/dateInput";

export interface SolicitacaoHistoricoFilterValues {
  search: string;
  dateFrom: string;
  dateTo: string;
  valorMin: string;
  valorMax: string;
  fornecedorFilter: string[];
  solicitanteFilter: string[];
  statusFilter: string[];
}

export const EMPTY_HISTORICO_FILTERS: SolicitacaoHistoricoFilterValues = {
  search: "",
  dateFrom: currentLocalDateInput(),
  dateTo: currentLocalDateInput(),
  valorMin: "",
  valorMax: "",
  fornecedorFilter: [],
  solicitanteFilter: [],
  statusFilter: [],
};

function itemHistoricoSearchText(item: SolicitacaoPagamentoDto): string {
  return [
    item.id,
    item.solicitanteName,
    item.creditorName,
    item.descricao,
    String(item.valor),
  ]
    .join(" ")
    .toLowerCase();
}

function parseValorFilter(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const normalized = s.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  /* 0 / 0,00 = campo vazio (placeholder); não restringe a lista. */
  if (n <= 0) return null;
  return n;
}

function fornecedorKey(item: SolicitacaoPagamentoDto): string {
  return item.creditorId?.trim() || item.creditorName?.trim() || "";
}

export function historicoFiltersAreActive(applied: SolicitacaoHistoricoFilterValues): boolean {
  return (
    applied.search.trim() !== "" ||
    applied.dateFrom !== "" ||
    applied.dateTo !== "" ||
    parseValorFilter(applied.valorMin) != null ||
    parseValorFilter(applied.valorMax) != null ||
    applied.fornecedorFilter.length > 0 ||
    applied.solicitanteFilter.length > 0 ||
    applied.statusFilter.length > 0
  );
}

export function buildHistoricoFilterOptions(
  items: SolicitacaoPagamentoDto[],
): {
  fornecedorOptions: MultiSelectOption[];
  solicitanteOptions: MultiSelectOption[];
  statusOptions: MultiSelectOption[];
} {
  const fornecedorMap = new Map<string, string>();
  const solicitanteMap = new Map<string, string>();

  for (const item of items) {
    const fk = fornecedorKey(item);
    if (fk) fornecedorMap.set(fk, item.creditorName || fk);
    if (item.solicitanteId) {
      solicitanteMap.set(item.solicitanteId, item.solicitanteName || item.solicitanteId);
    }
  }

  const fornecedorOptions = [...fornecedorMap.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

  const solicitanteOptions = [...solicitanteMap.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

  const statusOptions = (
    Object.entries(STATUS_LABELS) as [SolicitacaoPagamentoStatus, string][]
  )
    .filter(([value]) => value !== "cancelada")
    .map(([value, label]) => ({ value, label }));

  return { fornecedorOptions, solicitanteOptions, statusOptions };
}

export function filterSolicitacoesHistorico(
  items: SolicitacaoPagamentoDto[],
  applied: SolicitacaoHistoricoFilterValues,
): SolicitacaoPagamentoDto[] {
  const min = parseValorFilter(applied.valorMin);
  const max = parseValorFilter(applied.valorMax);
  const q = applied.search.trim().toLowerCase();

  return items.filter((item) => {
    if (q && !itemHistoricoSearchText(item).includes(q)) return false;

    const refDate = item.createdAt.slice(0, 10);
    if (applied.dateFrom && refDate < applied.dateFrom) return false;
    if (applied.dateTo && refDate > applied.dateTo) return false;

    if (min != null && item.valor < min) return false;
    if (max != null && item.valor > max) return false;

    if (applied.fornecedorFilter.length > 0) {
      const key = fornecedorKey(item);
      if (!applied.fornecedorFilter.includes(key)) return false;
    }

    if (applied.solicitanteFilter.length > 0) {
      if (!applied.solicitanteFilter.includes(item.solicitanteId)) return false;
    }

    if (applied.statusFilter.length > 0) {
      if (!applied.statusFilter.includes(item.status)) return false;
    }

    return true;
  });
}
